import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    IconButton,
    LinearProgress,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../SCUIButton/SCUIButton';
import { resolveS3ObjectKey } from '../../../pages/ImageSearchV2/ImageSearchV2.helper';
import {
    GetS3PresignedDownloadUrls,
    fetchBlobFromPresignedUrl,
    FetchS3FileBlob,
    saveBlobAsFile,
    saveBlobToDirectory,
    pickOrCreateDownloadSubfolder,
    canUseDirectoryPicker,
    getFileNameFromKey,
} from '../../../services/query/aiPrompt/S3BrowserQuery.services';

const DOWNLOAD_CONCURRENCY = 4;
const DOWNLOAD_MAX_ATTEMPTS = 3;
const DOWNLOAD_FOLDER_NAME = 'imageSearchV2';

enum DownloadItemStatus {
    Pending = 'Pending',
    Downloading = 'Downloading',
    Completed = 'Completed',
    Failed = 'Failed',
}

interface DownloadQueueItem {
    id: string;
    key: string;
    fileName: string;
    size: number;
    progress: number;
    status: DownloadItemStatus;
    error?: string;
    attempt: number;
    maxAttempts: number;
    presignedUrl?: string;
}

interface DownloadButtonProps {
    selectedItems: Array<any>;
    downloadLoading: boolean;
    setDownloadLoading: (value: boolean) => void;
    disabled?: boolean;
    children: (slots: { statusPanel: React.ReactNode; button: React.ReactNode }) => React.ReactNode;
}

function formatDownloadDuration(ms: number): string {
    if (!Number.isFinite(ms) || ms < 0) return '0:00';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) {
        return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
}

function collectS3Keys(selectedItems: Array<any>): { keys: string[]; skippedCount: number } {
    const keySet = new Set<string>();
    const keys: string[] = [];
    let skippedCount = 0;

    for (const item of selectedItems) {
        const metas = item?.imagePathIndexMetas;
        const key = resolveS3ObjectKey(metas?.uri, metas?.path);
        if (!key) {
            skippedCount += 1;
            continue;
        }
        if (keySet.has(key)) {
            continue;
        }
        keySet.add(key);
        keys.push(key);
    }

    return { keys, skippedCount };
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

async function resolveDownloadTargetDir(
    multiFile: boolean,
    t: TranslateFn
): Promise<FileSystemDirectoryHandle | null | undefined> {
    if (!multiFile) {
        return null;
    }

    if (!canUseDirectoryPicker()) {
        await Swal.fire({
            icon: 'info',
            title: t('aiPrompt.s3Browser.downloadFolderUnsupported'),
        });
        return null;
    }

    try {
        await Swal.fire({
            icon: 'info',
            title: t('aiPrompt.s3Browser.downloadPickFolderTitle'),
            text: t('aiPrompt.s3Browser.downloadPickFolderText', {
                folder: DOWNLOAD_FOLDER_NAME,
            }),
            confirmButtonText: t('general.ok'),
        });
        return await pickOrCreateDownloadSubfolder(DOWNLOAD_FOLDER_NAME);
    } catch (err) {
        const aborted =
            err instanceof DOMException &&
            (err.name === 'AbortError' || err.name === 'NotAllowedError');
        if (aborted) {
            await Swal.fire({
                icon: 'info',
                title: t('aiPrompt.s3Browser.downloadFolderCancelled'),
            });
            return undefined;
        }
        await Swal.fire({
            icon: 'warning',
            title: t('aiPrompt.s3Browser.downloadFolderFallback'),
        });
        return null;
    }
}

async function showDownloadResult(completed: number, failed: number, t: TranslateFn) {
    if (failed > 0 && completed === 0) {
        await Swal.fire({
            icon: 'error',
            title: t('general.error'),
            text: t('imageSearchV2.downloadError'),
        });
        return;
    }
    if (failed > 0) {
        await Swal.fire({
            icon: 'warning',
            title: t('imageSearchV2.downloadPartialFailTitle'),
            text: t('imageSearchV2.downloadPartialFailText', { completed, failed }),
        });
        return;
    }
    if (completed > 0) {
        await Swal.fire({
            icon: 'success',
            title: t('aiPrompt.s3Browser.downloadAllSuccess'),
            timer: 2000,
            showConfirmButton: false,
        });
    }
}

function getDownloadStatusText(status: DownloadItemStatus, t: TranslateFn) {
    switch (status) {
        case DownloadItemStatus.Downloading:
            return t('aiPrompt.s3Browser.downloadDownloading');
        case DownloadItemStatus.Completed:
            return t('aiPrompt.s3Browser.downloadCompleted');
        case DownloadItemStatus.Failed:
            return t('aiPrompt.s3Browser.downloadFailed');
        default:
            return t('aiPrompt.s3Browser.downloadReady');
    }
}

function getDownloadChipColor(status: DownloadItemStatus): 'default' | 'primary' | 'success' | 'error' {
    switch (status) {
        case DownloadItemStatus.Downloading:
            return 'primary';
        case DownloadItemStatus.Completed:
            return 'success';
        case DownloadItemStatus.Failed:
            return 'error';
        default:
            return 'default';
    }
}

export const SCUIDownloadImageV2Component: React.FC<DownloadButtonProps> = ({
    selectedItems,
    downloadLoading,
    setDownloadLoading,
    disabled = false,
    children,
}) => {
    const { t } = useTranslation();
    const translate = t as TranslateFn;
    const [downloadItems, setDownloadItems] = useState<DownloadQueueItem[]>([]);
    const [downloadStatusExpanded, setDownloadStatusExpanded] = useState(true);
    const [downloadElapsedMs, setDownloadElapsedMs] = useState(0);
    const [lastDownloadDurationMs, setLastDownloadDurationMs] = useState<number | null>(null);
    const [presignedCorsAvailable, setPresignedCorsAvailable] = useState<boolean | null>(null);

    const downloadTargetDirRef = useRef<FileSystemDirectoryHandle | null>(null);
    const downloadTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const downloadStartedAtRef = useRef<number | null>(null);

    const updateDownloadItem = useCallback((id: string, patch: Partial<DownloadQueueItem>) => {
        setDownloadItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
    }, []);

    const clearDownloadTimer = useCallback(() => {
        if (downloadTimerRef.current != null) {
            clearInterval(downloadTimerRef.current);
            downloadTimerRef.current = null;
        }
    }, []);

    const startDownloadTimer = useCallback(() => {
        clearDownloadTimer();
        downloadStartedAtRef.current = performance.now();
        setDownloadElapsedMs(0);
        setLastDownloadDurationMs(null);
        downloadTimerRef.current = setInterval(() => {
            if (downloadStartedAtRef.current != null) {
                setDownloadElapsedMs(Math.round(performance.now() - downloadStartedAtRef.current));
            }
        }, 250);
    }, [clearDownloadTimer]);

    const stopDownloadTimer = useCallback(() => {
        clearDownloadTimer();
        if (downloadStartedAtRef.current != null) {
            const duration = Math.round(performance.now() - downloadStartedAtRef.current);
            setLastDownloadDurationMs(duration);
            setDownloadElapsedMs(duration);
            downloadStartedAtRef.current = null;
        }
    }, [clearDownloadTimer]);

    useEffect(() => () => clearDownloadTimer(), [clearDownloadTimer]);

    useEffect(() => {
        if (!downloadLoading) return;
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            // eslint-disable-next-line
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [downloadLoading]);

    const runDownloadQueue = useCallback(
        async (items: DownloadQueueItem[], preferPresigned: boolean) => {
            setDownloadLoading(true);
            setDownloadStatusExpanded(true);

            let completed = 0;
            let failed = 0;

            try {
                let nextIndex = 0;
                const runWorker = async () => {
                    while (nextIndex < items.length) {
                        const index = nextIndex++;
                        const item = items[index];
                        if (!item) continue;

                        let success = false;
                        for (let attempt = item.attempt; attempt < item.maxAttempts; attempt += 1) {
                            updateDownloadItem(item.id, {
                                status: DownloadItemStatus.Downloading,
                                progress: attempt === item.attempt ? item.progress : 0,
                                attempt: attempt + 1,
                                error: undefined,
                            });

                            try {
                                let blob: Blob | null = null;

                                if (preferPresigned && item.presignedUrl && presignedCorsAvailable !== false) {
                                    try {
                                        blob = await fetchBlobFromPresignedUrl(item.presignedUrl, (loaded, total) => {
                                            const basis =
                                                total && total > 0 ? total : item.size > 0 ? item.size : loaded;
                                            const percent =
                                                basis > 0 ? Math.min(100, Math.round((loaded * 100) / basis)) : 0;
                                            updateDownloadItem(item.id, { progress: percent });
                                        });
                                        setPresignedCorsAvailable(true);
                                    } catch {
                                        setPresignedCorsAvailable(false);
                                    }
                                }

                                if (!blob) {
                                    blob = await FetchS3FileBlob(
                                        item.key,
                                        false,
                                        (evt: { loaded?: number; total?: number }) => {
                                            const loaded = evt.loaded ?? 0;
                                            const total = evt.total ?? item.size ?? 0;
                                            const percent =
                                                total > 0 ? Math.min(100, Math.round((loaded * 100) / total)) : 0;
                                            updateDownloadItem(item.id, { progress: percent });
                                        },
                                        undefined,
                                        { skipErrorModal: true }
                                    );
                                }

                                const fileName = item.fileName || getFileNameFromKey(item.key);
                                if (downloadTargetDirRef.current) {
                                    await saveBlobToDirectory(downloadTargetDirRef.current, fileName, blob);
                                } else {
                                    saveBlobAsFile(blob, fileName);
                                }
                                updateDownloadItem(item.id, {
                                    status: DownloadItemStatus.Completed,
                                    progress: 100,
                                });
                                success = true;
                                break;
                            } catch (err) {
                                const message = err instanceof Error ? err.message : String(err);
                                if (attempt + 1 >= item.maxAttempts) {
                                    updateDownloadItem(item.id, {
                                        status: DownloadItemStatus.Failed,
                                        error: message,
                                    });
                                } else {
                                    await new Promise((r) => setTimeout(r, 800));
                                }
                            }
                        }

                        if (success) {
                            completed += 1;
                        } else {
                            failed += 1;
                        }
                    }
                };

                await Promise.all(
                    Array.from({ length: Math.min(DOWNLOAD_CONCURRENCY, items.length) }, () => runWorker())
                );
            } finally {
                stopDownloadTimer();
                setDownloadLoading(false);
            }

            return { completed, failed };
        },
        [presignedCorsAvailable, setDownloadLoading, stopDownloadTimer, updateDownloadItem]
    );

    const startParallelDownloads = useCallback(
        async (keys: string[]) => {
            const initialItems: DownloadQueueItem[] = keys.map((key, index) => ({
                id: `${key}::${index}`,
                key,
                fileName: getFileNameFromKey(key),
                size: 0,
                progress: 0,
                status: DownloadItemStatus.Pending,
                attempt: 0,
                maxAttempts: DOWNLOAD_MAX_ATTEMPTS,
            }));

            setDownloadItems(initialItems);
            setDownloadStatusExpanded(true);
            downloadTargetDirRef.current = null;

            const targetDir = await resolveDownloadTargetDir(keys.length > 1, translate);
            if (targetDir === undefined) {
                setDownloadItems([]);
                return;
            }
            downloadTargetDirRef.current = targetDir;

            startDownloadTimer();

            let preferPresigned = true;
            let jobs = initialItems;
            try {
                const presigned = await GetS3PresignedDownloadUrls(keys);
                const urlByKey = new Map(presigned.items.map((i) => [i.key, i]));
                jobs = initialItems.map((item) => {
                    const match = urlByKey.get(item.key);
                    return {
                        ...item,
                        fileName: match?.fileName || item.fileName,
                        presignedUrl: match?.url,
                    };
                });
                setDownloadItems(jobs);
            } catch {
                preferPresigned = false;
            }

            const { completed, failed } = await runDownloadQueue(jobs, preferPresigned);
            await showDownloadResult(completed, failed, translate);
        },
        [runDownloadQueue, startDownloadTimer, translate]
    );

    const retryFailedDownloads = useCallback(async () => {
        const failedItems = downloadItems.filter((i) => i.status === DownloadItemStatus.Failed);
        if (!failedItems.length || downloadLoading) return;

        const reset = failedItems.map((item) => ({
            ...item,
            progress: 0,
            status: DownloadItemStatus.Pending,
            attempt: 0,
            error: undefined,
        }));

        setDownloadItems((prev) =>
            prev.map((item) => {
                const refreshed = reset.find((r) => r.id === item.id);
                return refreshed ?? item;
            })
        );

        startDownloadTimer();
        const { completed, failed } = await runDownloadQueue(reset, true);
        await showDownloadResult(completed, failed, translate);
    }, [downloadItems, downloadLoading, runDownloadQueue, startDownloadTimer, translate]);

    const handleDownloadClick = async () => {
        if (disabled || downloadLoading) {
            return;
        }
        if (selectedItems.length === 0) {
            Swal.fire(t('general.noSelectedDataFound'), ' ', 'error');
            return;
        }

        const { keys, skippedCount } = collectS3Keys(selectedItems);

        if (keys.length === 0) {
            await Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('imageSearchV2.downloadNoS3Keys'),
            });
            return;
        }

        if (skippedCount > 0) {
            await Swal.fire({
                icon: 'warning',
                title: t('imageSearchV2.downloadPartialSkipTitle'),
                text: t('imageSearchV2.downloadPartialSkipText', {
                    skipped: skippedCount,
                    ready: keys.length,
                }),
                confirmButtonText: t('general.ok'),
            });
        }

        await startParallelDownloads(keys);
    };

    const downloadCompletedCount = downloadItems.filter((i) => i.status === DownloadItemStatus.Completed).length;
    const downloadFailedCount = downloadItems.filter((i) => i.status === DownloadItemStatus.Failed).length;
    const showDownloadPanel = downloadItems.length > 0;

    const statusPanel = showDownloadPanel ? (
        <Box
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 20,
                mb: 2,
                flexShrink: 0,
                width: '100%',
            }}
        >
            <Accordion
                expanded={downloadStatusExpanded}
                onChange={(_, expanded) => setDownloadStatusExpanded(expanded)}
                sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                        <CloudDownloadIcon color="primary" />
                        <Typography sx={{ fontWeight: 600, flex: 1 }}>
                            {t('aiPrompt.s3Browser.downloadStatus')}
                        </Typography>
                        <Chip
                            size="small"
                            icon={<CloudDownloadIcon />}
                            label={`${downloadCompletedCount}/${downloadItems.length}`}
                            color={downloadFailedCount > 0 ? 'warning' : 'primary'}
                            variant="outlined"
                        />
                        {(downloadLoading || lastDownloadDurationMs !== null) && (
                            <Tooltip
                                title={
                                    downloadLoading
                                        ? String(
                                              t('aiPrompt.s3Browser.downloadElapsed', {
                                                  time: formatDownloadDuration(downloadElapsedMs),
                                              })
                                          )
                                        : String(
                                              t('aiPrompt.s3Browser.downloadTotalDuration', {
                                                  time: formatDownloadDuration(lastDownloadDurationMs!),
                                              })
                                          )
                                }
                                arrow
                                placement="top"
                            >
                                <Chip
                                    icon={
                                        downloadLoading ? (
                                            <HourglassEmptyOutlinedIcon
                                                sx={{ fontSize: 18, color: 'warning.main' }}
                                            />
                                        ) : (
                                            <AccessTimeIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                        )
                                    }
                                    label={formatDownloadDuration(
                                        downloadLoading ? downloadElapsedMs : lastDownloadDurationMs!
                                    )}
                                    size="small"
                                    sx={(theme) => ({
                                        fontWeight: 700,
                                        fontVariantNumeric: 'tabular-nums',
                                        bgcolor: downloadLoading
                                            ? alpha(theme.palette.warning.main, 0.14)
                                            : alpha(theme.palette.success.main, 0.14),
                                        color: downloadLoading
                                            ? theme.palette.warning.dark
                                            : theme.palette.success.dark,
                                        border: '1px solid',
                                        borderColor: downloadLoading
                                            ? theme.palette.warning.light
                                            : theme.palette.success.light,
                                        '& .MuiChip-icon': { ml: '4px' },
                                    })}
                                />
                            </Tooltip>
                        )}
                        {!downloadLoading && (
                            <IconButton
                                size="small"
                                aria-label={t('aiPrompt.s3Browser.downloadCloseStatus')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDownloadItems([]);
                                    setLastDownloadDurationMs(null);
                                    setDownloadElapsedMs(0);
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ maxHeight: 320, overflowY: 'auto', pt: 0 }}>
                    {downloadItems.map((item) => (
                        <Box
                            key={item.id}
                            sx={{
                                mb: 1.5,
                                p: 1.5,
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                backgroundColor: 'white',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ flex: 1, fontWeight: 600 }}
                                    noWrap
                                    title={item.fileName}
                                >
                                    {item.fileName}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={`${getDownloadStatusText(item.status, translate)} (${item.progress}%)`}
                                    color={getDownloadChipColor(item.status)}
                                />
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={item.progress}
                                color={item.status === DownloadItemStatus.Failed ? 'error' : 'primary'}
                            />
                            {item.error && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                    {item.error}
                                </Typography>
                            )}
                        </Box>
                    ))}
                    {!downloadLoading && downloadFailedCount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ flex: 1, alignSelf: 'center' }}
                            >
                                {t('aiPrompt.s3Browser.downloadPartialMessage', {
                                    failed: downloadFailedCount,
                                })}
                            </Typography>
                            <SCUIButton
                                type="primary"
                                sizeType="default"
                                size="s"
                                text={String(t('aiPrompt.s3Browser.downloadRetryFailed'))}
                                onClick={() => void retryFailedDownloads()}
                                showStartIcon
                                startIcon={<ReplayIcon />}
                            />
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    ) : null;

    const button = (
        <SCUIButton
            onClick={handleDownloadClick}
            type="primary"
            sizeType="fixed"
            size="l"
            text={
                downloadLoading
                    ? `${String(t('imageSearchV2.export'))}...`
                    : `${String(t('imageSearchV2.export'))} (${selectedItems.length})`
            }
            disabled={disabled || downloadLoading}
        />
    );

    return <>{children({ statusPanel, button })}</>;
};
